import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Canvas,
  pdf,
} from "@react-pdf/renderer";

// ─── Constantes ───────────────────────────────────────────────────────────────

const BORDER     = "0.5pt solid #000";
const BORDER_CLR = "#000000";
const BORDER_W   = 0.5;
const FONT_SIZE  = 6.5;
const HEADER_H   = 72;
const HEADER_BG  = "#BDD7EE";
const TOTAL_BG   = "#FFF2CC";

const CHAR_W     = FONT_SIZE * 0.50;
const CELL_PAD   = 4;
const PAGE_W     = 841.89 - 18 * 2;

// ─── Calcul dynamique des largeurs ───────────────────────────────────────────

const textW = (str) => String(str).length * CHAR_W + CELL_PAD;

const computeWidths = (students, matieres) => {

  const FIXED_PAD = 12;

  // 🔁 N° remplacé par Matricule
  const W_NO     = Math.max(textW("Matricule"), ...students.map((s) => textW(s.no))) + FIXED_PAD;
  const W_PRENOM = Math.max(textW("Prénom"), ...students.map((s) => textW(s.prenom))) + FIXED_PAD;
  const W_NOM    = Math.max(textW("Nom"), ...students.map((s) => textW(s.nom))) + FIXED_PAD;
  const W_SEXE   = Math.max(textW("Sexe"), ...students.map((s) => textW(s.s))) + FIXED_PAD;

  const W_FIXED  = W_NO + W_PRENOM + W_NOM + W_SEXE;

  const W_NOTE_DATA  = Math.max(...students.flatMap((s) => s.notes.map((n) => textW(n))));
  const W_TOTAL_DATA = Math.max(textW("TOTAL"),   ...students.map((s) => textW(s.total)));
  const W_MOY_DATA   = Math.max(textW("MOYENNE"), ...students.map((s) => textW(s.moy)));

  const rawVar  = [...matieres.map(() => W_NOTE_DATA), W_TOTAL_DATA, W_MOY_DATA];
  const sumRaw  = rawVar.reduce((a, b) => a + b, 0);
  const scale   = (PAGE_W - W_FIXED) / sumRaw;
  const VAR_W   = rawVar.map((w) => w * scale);

  const sumVar  = VAR_W.reduce((a, b) => a + b, 0);
  VAR_W[VAR_W.length - 1] += (PAGE_W - W_FIXED) - sumVar;

  return {
    W_NO, W_PRENOM, W_NOM, W_SEXE,
    W_NOTES:   VAR_W.slice(0, matieres.length),
    W_TOTAL:   VAR_W[matieres.length],
    W_MOYENNE: VAR_W[matieres.length + 1],
  };
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  page: {
    paddingTop: 18,
    paddingBottom: 18,
    paddingHorizontal: 18,
    fontSize: FONT_SIZE,
    fontFamily: "Helvetica",
    backgroundColor: "#fff",
  },
  headerRow:       { flexDirection: "row", alignItems: "center", marginBottom: 4 },
  logoBox:         { width: 36, height: 36, border: BORDER, borderRadius: 18, marginRight: 8, alignItems: "center", justifyContent: "center" },
  logoText:        { fontSize: 5, textAlign: "center", color: "#333" },
  headerTextBlock: { flex: 1, alignItems: "center" },
  headerTitle:     { fontSize: 8, fontFamily: "Helvetica-Bold", textAlign: "center", marginBottom: 1 },
  headerSubtitle:  { fontSize: 7, textAlign: "center", marginBottom: 1 },
  headerMention:   { fontSize: 7, textAlign: "center", fontFamily: "Helvetica-Bold" },
  table:           { width: "100%", borderLeft: BORDER, borderTop: BORDER },
  row:             { flexDirection: "row" },
  cell: {
    borderRight: BORDER,
    borderBottom: BORDER,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 1,
    paddingVertical: 1,
  },
  cellText:     { fontSize: FONT_SIZE, textAlign: "center" },
  cellTextBold: { fontSize: FONT_SIZE, fontFamily: "Helvetica-Bold", textAlign: "center" },
  cellTextLeft: { fontSize: FONT_SIZE, textAlign: "left", paddingLeft: 2 },
  headerHeight: { height: HEADER_H },
  rowEven:      { backgroundColor: "#EAF3FB" },
  rowOdd:       { backgroundColor: "#fff" },
  totalCell:    { backgroundColor: TOTAL_BG },
  footer:       { marginTop: 8, flexDirection: "row", justifyContent: "space-between" },
  footerText:   { fontSize: 7 },
});

// ─── Composants ──────────────────────────────────────────────────────────────

const Cell = ({ style, children, textStyle }) => (
  <View style={[styles.cell, style]}>
    <Text style={[styles.cellText, textStyle]}>{children ?? ""}</Text>
  </View>
);

const VerticalHeaderCell = ({ label, width, bg = HEADER_BG }) => {
  const painter = (p, w, h) => {
    p.rect(0, 0, w, h).fill(bg);
    p.lineWidth(BORDER_W).strokeColor(BORDER_CLR);
    p.moveTo(0, 0).lineTo(w, 0).stroke();
    p.moveTo(0, h).lineTo(w, h).stroke();
    p.moveTo(w, 0).lineTo(w, h).stroke();
    p.moveTo(0, 0).lineTo(0, h).stroke();
    p.save();
    p.translate(w / 2, h / 2);
    p.rotate(-90);
    p.fontSize(FONT_SIZE)
     .font("Helvetica-Bold")
     .fillColor("#000000")
     .text(label, -(h / 2) + 4, -(FONT_SIZE / 2), {
       width:     h - 8,
       align:     "center",
       lineBreak: false,
     });
    p.restore();
  };
  return <Canvas style={{ width, height: HEADER_H }} paint={painter} />;
};

// ─── Document PDF ─────────────────────────────────────────────────────────────

const ResultatsPharmaciePDF = ({
  sessionResults = [],
  ueColumns      = [],   // ✅ UE
  sessionInfo    = null,
}) => {

  const matieres = ueColumns.map((ue) => ue.ue_label);

  const students = sessionResults.map((result, index) => {
    const notes = ueColumns.map((ue) => {
      const found = (result.ue_notes || []).find((n) => n.ue_id === ue.ue_id);
      return found?.moyenne !== null && found?.moyenne !== undefined
        ? parseFloat(found.moyenne)
        : "-";
    });

    return {
      no:     result.student?.student_number ?? "-", // 🔁 Matricule ici
      prenom: result.student?.first_name  ?? "-",
      nom:    result.student?.last_name   ?? "-",
      s:      result.student?.sex         ?? "-",
      notes,
      total:  parseFloat(result.total).toFixed(2),
      moy:    parseFloat(result.moyenne).toFixed(2),
    };
  });

  const widths = students.length > 0
    ? computeWidths(students, matieres)
    : null;

  if (!widths || students.length === 0) {
    return (
      <Document>
        <Page size="A4" orientation="landscape" style={styles.page}>
          <Text>Aucune donnée à afficher.</Text>
        </Page>
      </Document>
    );
  }

  const { W_NO, W_PRENOM, W_NOM, W_SEXE, W_NOTES, W_TOTAL, W_MOYENNE } = widths;

  const HeaderRow = () => (
    <View style={styles.row}>
      <Cell style={[{ width: W_NO }, styles.headerHeight, { backgroundColor: HEADER_BG }]}
            textStyle={{ fontFamily: "Helvetica-Bold" }}>Matricule</Cell>

      <Cell style={[{ width: W_PRENOM }, styles.headerHeight, { backgroundColor: HEADER_BG }]}
            textStyle={{ fontFamily: "Helvetica-Bold" }}>Prénom</Cell>

      <Cell style={[{ width: W_NOM }, styles.headerHeight, { backgroundColor: HEADER_BG }]}
            textStyle={{ fontFamily: "Helvetica-Bold" }}>Nom</Cell>

      <Cell style={[{ width: W_SEXE }, styles.headerHeight, { backgroundColor: HEADER_BG }]}
            textStyle={{ fontFamily: "Helvetica-Bold" }}>Sexe</Cell>

      {matieres.map((m, i) => (
        <VerticalHeaderCell key={i} label={m} width={W_NOTES[i]} />
      ))}

      <VerticalHeaderCell label="TOTAL" width={W_TOTAL} />
      <VerticalHeaderCell label="MOYENNE" width={W_MOYENNE} />
    </View>
  );

  const StudentRow = ({ student, index }) => {
    const bg = index % 2 === 0 ? styles.rowEven : styles.rowOdd;
    return (
      <View style={[styles.row, bg]}>
        <Cell style={{ width: W_NO }}
              textStyle={{ fontFamily: "Helvetica-Bold" }}>{student.no}</Cell>

        <Cell style={[{ width: W_PRENOM }, { alignItems: "flex-start" }]}
              textStyle={styles.cellTextLeft}>{student.prenom}</Cell>

        <Cell style={[{ width: W_NOM }, { alignItems: "flex-start" }]}
              textStyle={[styles.cellTextLeft, { fontFamily: "Helvetica-Bold" }]}>{student.nom}</Cell>

        <Cell style={{ width: W_SEXE }}>{student.s}</Cell>

        {student.notes.map((n, i) => (
          <Cell key={i} style={{ width: W_NOTES[i] }}>{n}</Cell>
        ))}

        <Cell style={[{ width: W_TOTAL }, styles.totalCell]}
              textStyle={{ fontFamily: "Helvetica-Bold" }}>{student.total}</Cell>

        <Cell style={[{ width: W_MOYENNE }, styles.totalCell]}
              textStyle={{ fontFamily: "Helvetica-Bold" }}>{student.moy}</Cell>
      </View>
    );
  };

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page}>

        <View style={styles.headerRow}>
          <View style={styles.headerTextBlock}>
            <Text style={styles.headerTitle}>
              UNIVERSITE DES SCIENCES, DES TECHNIQUES
            </Text>
            <Text style={styles.headerTitle}>
              ET DES TECHNOLOGIES DE BAMAKO : USTTB
            </Text>
            <Text style={styles.headerTitle}>
              FACULTE DE MEDECINE ET D'ODONTO-STOMATOLOGIE : FMOS
            </Text>
            <Text style={styles.headerSubtitle}>
              {sessionInfo
                ? `Résultats — ${sessionInfo.label} · ${sessionInfo.academic_year}`
                : "Résultats de session"}
            </Text>
            <Text style={styles.headerMention}>
              Liste des étudiants
            </Text>
          </View>
        </View>

        <View style={styles.table}>
          <HeaderRow />
          {students.map((s, i) => (
            <StudentRow key={i} student={s} index={i} />
          ))}
        </View>

        <View style={styles.footer}>
          <Text style={styles.footerText}>
            {new Date().toLocaleDateString("fr-FR", {
              day: "2-digit", month: "long", year: "numeric"
            })}
          </Text>
          <Text style={styles.footerText}>Président du jury</Text>
          <Text style={styles.footerText}>Le Recteur</Text>
        </View>

      </Page>
    </Document>
  );
};

export default ResultatsPharmaciePDF;

// ✅ generatePDF adapté UE
export async function generatePDF({ sessionResults = [], ueColumns = [], sessionInfo = null }) {
  const blob = await pdf(
    <ResultatsPharmaciePDF
      sessionResults={sessionResults}
      ueColumns={ueColumns}
      sessionInfo={sessionInfo}
    />
  ).toBlob();

  const label    = sessionInfo?.label?.replace(/\s+/g, "_") ?? "session";
  const annee    = sessionInfo?.academic_year ?? "resultats";
  const filename = `resultats_${label}_${annee}.pdf`;

  const url = URL.createObjectURL(blob);
  const a   = document.createElement("a");
  a.href     = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}